import { gql, useMutation } from '@apollo/client';
import type {
  FileDocumentObjectTypes,
  FileDocumentTypes,
} from '@generated/graphql';
import { useToast } from 'hephaestus/web';
import { uploadWithRetry } from '../utils/fileUploader';

export type CreateFileInput = {
  objectType: FileDocumentObjectTypes;
  objectId: string;
  fileName: string;
  type: FileDocumentTypes;
  userId?: string;
  name?: string;
  permissionType?: string;
  mediaType?: string;
  sourceId?: string;
  sourceType?: string;
  sharedClient?: boolean;
  sharedWithRoleProfiles?: string[];
  organizationId?: string;
};

export const CREATE_FILE_DOCUMENT = gql`
  mutation createFileDocument($input: CreateFileDocumentInput!) {
    createFileDocument(input: $input) {
      fileDocument {
        id
        name
        fileName
        s3Key
        type
        uploadUrl
      }
    }
  }
`;

export const useFileUpload = () => {
  const [createFileDocument] = useMutation(CREATE_FILE_DOCUMENT);
  const { enqueueToast } = useToast();

  const uploadFile = async (
    file: File,
    input: CreateFileInput,
  ): Promise<string> => {
    const { data } = await createFileDocument({
      variables: {
        input: {
          fileName: input.fileName,
          objectId: input.objectId,
          objectType: input.objectType,
          name: input.name,
          type: input.type,
          permissionType: input.permissionType,
          mediaType: input.mediaType,
          sourceId: input.sourceId,
          sourceType: input.sourceType,
          organizationId: input.organizationId,
        },
      },
    });
    const uploadUrl = data?.createFileDocument?.fileDocument?.uploadUrl;

    try {
      const uploaded: Response = await uploadWithRetry(uploadUrl, file, 'Form');
      if (!uploaded.ok)
        throw new Error(`${uploaded.status} ${uploaded.statusText}`);
    } catch {
      enqueueToast('Upload failed', { variant: 'error' });
      return '';
    }

    return data.createFileDocument.fileDocument.s3Key;
  };

  return { uploadFile };
};
